import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Button, Select as AntSelect } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { Scatter } from 'react-chartjs-2';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  DAYS_WINDOW_OPTIONS,
  getPipelineHealthVolume,
  getPipelineHealthSources,
  getPipelineHealthDomains
} from '../../../../api/pipelineHealthApi';
import { formatDomain } from '../pipelineHealthUtils';
import { thousand } from '../../../../functions';

function zscoreColor(z) {
  if (z == null) return '#9ca3af';
  const abs = Math.abs(z);
  if (z > 0) {
    if (abs >= 100) return '#7c2d12';
    if (abs >= 3) return '#dc2626';
    if (abs >= 2) return '#f59e0b';
    return '#0ea5e9';
  }
  if (abs >= 3) return '#dc2626';
  if (abs >= 2) return '#f59e0b';
  return '#0ea5e9';
}

function VolumeAnalytics() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topZScored, setTopZScored] = useState(10);
  const [daysWindow, setDaysWindow] = useState(7);
  const [sources, setSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainFilter, setDomainFilter] = useState('');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Sources + domains feed the optional sourceFilter / domain dropdowns.
  useEffect(() => {
    getPipelineHealthSources()
      .then((res) => setSources(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
    getPipelineHealthDomains()
      .then((res) => setDomains(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, []);

  const load = () => {
    setLoading(true);
    getPipelineHealthVolume(daysWindow, topZScored, sourceFilter, domainFilter)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [topZScored, daysWindow, sourceFilter, domainFilter]);

  const daysOptions = DAYS_WINDOW_OPTIONS.map((d) => ({
    value: d,
    label: t(`pipeline_health_days_${d}`)
  }));
  const sourceOptions = [
    { value: '', label: t('pipeline_health_all_sources') },
    ...sources?.map((s) => ({ value: s.source, label: formatDomain(s.source) }))
  ];

  // Domain options narrow to the selected source's domains. With no source
  // filter we take the union from /sources, falling back to /domains if
  // /sources hasn't loaded yet.
  const availableDomains = useMemo(() => {
    if (sourceFilter) {
      const src = sources.find((s) => s.source === sourceFilter);
      return src?.domains || [];
    }
    if (sources.length > 0) {
      return Array.from(new Set(sources.flatMap((s) => s.domains || []))).sort();
    }
    return domains;
  }, [sources, sourceFilter, domains]);

  // If the user picks a source that doesn't include the currently-selected
  // domain, drop the domain filter back to "All".
  useEffect(() => {
    if (domainFilter && !availableDomains.includes(domainFilter)) {
      setDomainFilter('');
    }
  }, [availableDomains, domainFilter]);

  const domainOptions = [
    { value: '', label: t('pipeline_health_all_domains') },
    ...availableDomains.map((d) => ({ value: d, label: formatDomain(d) }))
  ];

  // ---- Scatter chart ----------------------------------------------------

  const scatterChart = useMemo(() => {
    const points = data?.scatter || [];
    // Keep any row where at least one side is non-zero — a baseline-of-5 with
    // records-of-0 is meaningful (volume dropped to zero), not noise. The
    // log10(1 + x) transform handles zeros without going to -Infinity and
    // preserves the y = x reference line (records = baseline → both equal).
    const projected = points
      .filter((p) => (p.recordsProcessed ?? 0) > 0 || (p.baseline7dAvg ?? 0) > 0)
      .map((p) => ({
        x: Math.log10(1 + Number(p.baseline7dAvg)),
        y: Math.log10(1 + Number(p.recordsProcessed)),
        raw: p
      }));

    const normal = projected.filter((p) => !p.raw.isAnomaly);
    const anomaly = projected.filter((p) => p.raw.isAnomaly);

    // y = x reference line so the eye can see how far each point is from a
    // "no variation" baseline. Built from a single segment covering the
    // full x range plus a small pad.
    const xs = projected.map((p) => p.x);
    const minX = xs.length > 0 ? Math.min(...xs) : 0;
    const maxX = xs.length > 0 ? Math.max(...xs) : 1;
    const pad = Math.max(0.1, (maxX - minX) * 0.05);
    const reference = [
      { x: minX - pad, y: minX - pad },
      { x: maxX + pad, y: maxX + pad }
    ];

    return {
      datasets: [
        {
          label: t('pipeline_health_volume_normal'),
          data: normal,
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          pointRadius: 5,
          pointHoverRadius: 7
        },
        {
          label: t('pipeline_health_volume_anomaly'),
          data: anomaly,
          backgroundColor: '#dc2626',
          borderColor: '#dc2626',
          pointRadius: 6,
          pointHoverRadius: 8
        },
        {
          label: t('pipeline_health_volume_reference'),
          data: reference,
          showLine: true,
          borderColor: 'rgba(148, 163, 184, 0.8)',
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false
        }
      ]
    };
  }, [data, t]);

  const scatterOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const raw = ctx.raw?.raw;
            if (!raw) {
              // The reference line points don't carry raw data.
              return `${ctx.dataset.label}: (${ctx.parsed.x.toFixed(2)}, ${ctx.parsed.y.toFixed(2)})`;
            }
            return [
              `${raw.entityName} (${formatDomain(raw.domain)})`,
              `${t('pipeline_health_records')}: ${thousand(raw.recordsProcessed ?? 0)}`,
              `${t('pipeline_health_baseline')}: ${thousand(Math.round(Number(raw.baseline7dAvg) || 0))}`,
              `${t('pipeline_health_zscore')}: ${raw.volumeZscore == null ? '—' : Number(raw.volumeZscore).toFixed(2)}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: t('pipeline_health_volume_x_axis') },
        type: 'linear'
      },
      y: {
        title: { display: true, text: t('pipeline_health_volume_y_axis') },
        type: 'linear'
      }
    }
  }), [t]);

  // ---- Top z-scores table ----------------------------------------------

  const topColumns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    { title: t('domain'), dataIndex: 'domain', key: 'domain', width: 130, render: formatDomain },
    {
      title: t('pipeline_health_date'),
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: 130,
      render: (d) => (d ? dayjs(d).format('MMM DD, YYYY') : '—')
    },
    {
      title: t('pipeline_health_records'),
      dataIndex: 'recordsProcessed',
      key: 'recordsProcessed',
      width: 120,
      align: 'right',
      render: (v) => (v == null ? '—' : thousand(v))
    },
    {
      title: t('pipeline_health_baseline'),
      dataIndex: 'baseline7dAvg',
      key: 'baseline7dAvg',
      width: 120,
      align: 'right',
      render: (v) => (v == null ? '—' : thousand(Math.round(Number(v))))
    },
    {
      title: t('pipeline_health_zscore'),
      dataIndex: 'volumeZscore',
      key: 'volumeZscore',
      width: 110,
      align: 'right',
      render: (v) => (
        <span style={{ color: zscoreColor(v == null ? null : Number(v)), fontWeight: 600 }}>
          {v == null ? '—' : Number(v).toFixed(2)}
        </span>
      )
    },
    {
      title: t('pipeline_health_diagnostic'),
      dataIndex: 'diagnostic',
      key: 'diagnostic'
    }
  ];

  // -----------------------------------------------------------------------

  const scatterCount = (data?.scatter || []).length;
  const topRows = data?.topZScores || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_days_window')}:</span>
        <AntSelect
          value={daysWindow}
          disabled={loading}
          onChange={setDaysWindow}
          style={{ width: 150 }}
          options={daysOptions}
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_source_filter')}:</span>
        <AntSelect
          value={sourceFilter}
          disabled={loading}
          onChange={setSourceFilter}
          style={{ minWidth: 160 }}
          options={sourceOptions}
        />
        <span className="text-sm text-gray-600">{t('domain')}:</span>
        <AntSelect
          value={domainFilter}
          disabled={loading}
          onChange={setDomainFilter}
          style={{ minWidth: 160 }}
          options={domainOptions}
        />
        <span className="text-sm text-gray-500">
          {t('pipeline_health_volume_top_n_label')}:
        </span>
        <AntSelect
          value={topZScored}
          disabled={loading}
          onChange={setTopZScored}
          style={{ width: 80 }}
          options={[
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 15, label: '15' },
            { value: 20, label: '20' }
          ]}
        />
        {data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        {data?.datasetStart && !data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetStart).add(30, 'days').format('MMM DD, YYYY')}
          </span>
        )}
        {!data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetEnd).add(-30, 'days').format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        <Button
          className="ml-auto"
          type="primary"
          onClick={load}
          loading={loading}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4">

          {/* Scatter */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_volume_scatter')}</div>
            {scatterCount === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <Spin spinning={loading}>
                <div style={{ height: 380 }}>
                  <Scatter data={scatterChart} options={scatterOptions} />
                </div>
              </Spin>
            )}
          </div>

          {/* Top Z table */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">
              {t('pipeline_health_volume_top_z')} ({topZScored})
            </div>
            {topRows.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <Table
                size="small"
                loading={loading}
                rowKey={(row, idx) => `${row.entityName}-${row.eventDate}-${idx}`}
                columns={topColumns}
                dataSource={topRows}
                pagination={false}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VolumeAnalytics;
